import { action, runInAction, toJS, makeObservable } from 'mobx';
import MovimentoApi from '../api/MovimentoApi';
import { Minuta, Processo } from '../modelo';
import { ComplementoGEDTO, ComplementosPossiveisDto, MinutaMovimentos, MovimentoBasicoDTO, MovimentoGEDTO } from '../modelo/MinutaMovimentos';
import MensagemStore from '../store/MensagemStore';
import MovimentoCNJStore from '../store/MovimentoCNJStore';
import ProcessoService from './ProcessoService';

const VARIAVEIS_COMPLEMENTO_POR_POLO = {
    'nome da parte': ['polo ativo', 'polo passivo', 'outros participantes'],
    'nome do recurso': ['polo ativo', 'polo passivo', 'outros participantes'],
};

const VARIAVEIS_COMPLEMENTO_POR_POLO_UNITARIO = {
    'nome da parte': ['polo ativo', 'polo passivo', 'outros participantes'],
};

class MovimentoService {
    carregarMovimentos = async () => {
        const movimentos = await this.recuperarTodosLegadoPJe();
        movimentos.sort((m1, m2) => m1.codMovimento < m2.codMovimento ? -1 : 0);
        runInAction(() => {
            MovimentoCNJStore.movimentos = movimentos.map(m => MovimentoGEDTO.toGEDTO(m, true));
        });
    }

    constructor() {
        makeObservable(this, {
            ajustarComplementos: action,
            incluirMovimentoSelecionado: action,
            continuarInclusao: action,
            removerMovimento: action
        });
    }

    async recuperarTodosLegadoPJe(): Promise<MovimentoBasicoDTO[]> {
        return await MovimentoApi.recuperarTodosLegadoPJe();
    }

    async recuperar(codigoMovimentoCNJ: string): Promise<MovimentoGEDTO> {
        const todosmovimentos = MovimentoCNJStore.movimentos;
        const movimentos = [...todosmovimentos];

        const filtrados = movimentos.filter(m => m.codMovimentoCnj === codigoMovimentoCNJ);
        const filtrado = filtrados.length > 0 ? filtrados[0] : undefined;

        return filtrado;
    }

    async recuperarComIdentificadorProcesso(identificadorProcesso) {
        return MovimentoApi.recuperarMovimentosMinutasComIdentificadorProcesso(identificadorProcesso);
    }



    async gravar(minutasMovimentos: Array<MinutaMovimentos>) {
        const minutasMov = minutasMovimentos.slice().map(mm => {
            mm.movimentos = mm.movimentos.map(mov => {
                mov.complementos = mov.complementos?.filter(comp => comp.valorSetado);
                return mov;
            });
            return mm;
        });
        const result = await MovimentoApi.gravar(minutasMov);
        runInAction(() => {
            MovimentoCNJStore.complementosAutomaticosSetados = false;
            MovimentoCNJStore.sameState = false;
        })
        return result;
    }

    async recuperarMovimentosMinutas(processos: Processo[], carregarComplementos = true): Promise<MovimentoGEDTO[][]> {
        const minutasMovimentos: MinutaMovimentos[] = await MovimentoApi.recuperarMovimentosMinutas(processos);
        const mapNumMinutaProcesso: any = [];
        const movimentos: MovimentoGEDTO[] = [];

        minutasMovimentos.forEach(mm => {
            const processo = processos.find(proc => proc.minutas.find(minuta => minuta.numero === mm.numMinuta));
            if (processo) {
                mapNumMinutaProcesso[mm.numMinuta] = processo;
            }
            mm.movimentos = mm.movimentos.map(movimento => MovimentoGEDTO.novo(movimento, processo));
        });
        if (carregarComplementos) {
            minutasMovimentos.forEach(mm => {

                const processo = mapNumMinutaProcesso[mm.numMinuta];

                mm.movimentos = mm.movimentos.map(mov => {

                    const movCNJ: MovimentoGEDTO = MovimentoCNJStore.movimentos.find(movimentoCNJ => movimentoCNJ.codMovimentoCnj === mov.codMovimentoCnj);
                    mov.descricaoMovimento = movCNJ.descricaoMovimento;
                    mov.origem = movCNJ.origem;
                    mov = MovimentoGEDTO.novo(mov, mapNumMinutaProcesso[mov.numMinuta]);
                    this.recuperarOpcoesEComplementos(processo, mov).then(() => {
                        this.ajustarComplementos(mov, movCNJ);
                    });
                    movimentos.push(mov)
                    return mov;
                });

            });

            runInAction(() => {
                MovimentoCNJStore.movimentosMinutas = minutasMovimentos;
            });
        }
        const retorno = [];
        minutasMovimentos.forEach(mm => retorno[mm.numMinuta] = mm.movimentos);
        return retorno;

    }

    ajustarComplementos = (movimento: MovimentoGEDTO, movimentoCNJOriginal: MovimentoGEDTO) => {
        movimento.complementos = movimento.complementos?.map(complemento => {
            const comp: ComplementoGEDTO = movimentoCNJOriginal.complementos?.find(complementoCNJ => complementoCNJ.codElementoDominio === complemento.codElementoDominio);

            complemento.codElementoDominio = comp?.codElementoDominio || complemento.codElementoDominio;
            complemento.codComplementoCnj = comp?.codComplementoCnj || complemento.codComplementoCnj;
            complemento.stringComplemento = comp?.stringComplemento
                || complemento.stringComplemento
                || movimento.complementosPossiveis?.find(compPossivel => compPossivel.codElementoDominio === complemento.codElementoDominio)?.stringComplemento;

            // TODO que porra é essa aqui? arruma essa merda por favor
            complemento.valorSetado = comp?.valor || complemento.valorSetado || (complemento.valorComplemento.length > 0 && complemento.valorComplemento[0] === '{' && JSON.parse(complemento.valorComplemento).valor) || complemento.valorComplemento;

            complemento.valor = comp?.valor || complemento.valor || complemento.valorSetado;

            return complemento;
        }) || [];
    };

    async recuperarOpcoesEComplementos(processo: Processo, movimentoCNJ: MovimentoGEDTO, apenasRetornar: boolean = false): Promise<ComplementosPossiveisDto[]> {
        const result = await MovimentoApi.recuperarOpcoesDeComplementos(processo, movimentoCNJ.codMovimentoCnj);
        const complementosPossiveis = result.map(r => ComplementosPossiveisDto.ToComplementoPossivelDto(r));
        if (!apenasRetornar) {
            runInAction(() => {
                movimentoCNJ.complementosPossiveis = complementosPossiveis;
            });
        }
        return complementosPossiveis;
    }

    getMinutaSelecionada(processo: Processo): Minuta {
        return processo.minutas?.find(minuta => minuta.tipo.codigo === MovimentoCNJStore.codigoTipoMinutaSelecionado);
    }

    getMovimentosMinutaSelecionada(processo: Processo): MovimentoGEDTO[] {
        if (!processo) {
            return [];
        }
        const minuta = this.getMinutaSelecionada(processo);
        const movimentos = MovimentoCNJStore.movimentosMinutas?.find(mp => mp.numMinuta === minuta?.numero)?.movimentos || [];
        return movimentos;
    }

    getMovimentosMinutaSelecionadaPorMinuta(minutaSelecionada: Minuta): MovimentoGEDTO[] {
        return minutaSelecionada?.movimentosCNJ || [];
    }

    getDescricaoMovimento(movimento: MovimentoGEDTO, _processo: Processo): string {
        let descricao = '';
        descricao = movimento?.descricaoMovimento || movimento.descricaoMovimentoUnificacao;
        movimento.variaveis?.forEach(variavel => {
            let valorVariavel = this.getValorDeComplementoPorVariavel(movimento, variavel);
            if (valorVariavel) {
                try {
                    const parse = JSON.parse(valorVariavel)
                    valorVariavel = parse?.valor ? parse.valor : parse;
                } catch (_e) {
                    // valorVariavel = valorVariavel;
                }
                descricao = descricao.replace('#{' + variavel + '}', valorVariavel);
            }
        });
        return descricao;
    }

    getValorDeComplementoPorVariavel(movimento: MovimentoGEDTO, variavelDominio: string): string {
        const complemento = movimento.complementos?.find(comp => comp.stringComplemento?.toLowerCase() === variavelDominio?.toLowerCase());
        return complemento?.valorSetado || '';
    }

    variavelExcludentePreenchida(nomeDominio: string, movimento: MovimentoGEDTO, _processo: Processo) {
        const excludente = this.existeComplementoExcludente(nomeDominio, movimento);
        return excludente?.some(exc => this.getValorDeComplementoPorVariavel(movimento, exc));
    }

    existeComplementoExcludente(nomeDominio: string, movimento: MovimentoGEDTO) {
        const dominios = this.getMovimentosExcludentes(movimento);
        const excludente = dominios?.find(dominio => dominio.includes(nomeDominio))?.filter(exc => exc !== nomeDominio);
        return excludente;
    }

    getMovimentosExcludentes(movimento: MovimentoGEDTO): string[][] {

        const descricaoMovimento = movimento?.descricaoMovimento || '';
        return descricaoMovimento.match(/#\{[A-Za-zÀ-ÖØ-öø-ÿ\s]*}(?:\s*\/\s*(#\{)?[A-Za-zÀ-ÖØ-öø-ÿ\s]*}){1,}/gi)?.map(a => a.replace(/#{|}/g, '').split('/').map(b => b.trim()));
    }

    private descricaoContemVariavelRelevante(descricao: string): boolean {
        return Object.keys(VARIAVEIS_COMPLEMENTO_POR_POLO).some(variavel => descricao.includes(`#{${variavel}}`));
    }

    private descricaoContemVariavelRelevanteUnitario(descricao: string): boolean {
        return Object.keys(VARIAVEIS_COMPLEMENTO_POR_POLO_UNITARIO).some(variavel => descricao.includes(`#{${variavel}}`));
    }

    incluirMovimentoSelecionado = async (movimentoSelecionado: MovimentoGEDTO, legado: boolean, lote: boolean) => {
        if (!lote) {
            MovimentoCNJStore.processos
                .filter(processo => processo.ehLegado == legado)
                .forEach(async processo => {
                    if (!processo.partes) {
                        await ProcessoService.carregarPartesProcesso(processo);
                    }
                    console.log(processo)
                    const movSelecionado = MovimentoGEDTO.novo(movimentoSelecionado, processo);
                    if (this.descricaoContemVariavelRelevanteUnitario(movSelecionado.descricaoMovimento)) {
                        runInAction(() => {
                            MovimentoCNJStore.openModalComplementos = true;
                        });

                        const complementosPossiveis = await this.recuperarOpcoesEComplementos(processo, movSelecionado, false);

                        runInAction(() => {
                            MovimentoCNJStore.movimentoPJeSelecionado.complementosPossiveis = complementosPossiveis;
                        });

                        //criar movimentos para cada parte selecionada
                        const movimentosCriados = this.criarMovimentosComplementados(processo, movSelecionado, complementosPossiveis);

                        const minutaSelecionada = this.getMinutaSelecionada(processo);
                        if (minutaSelecionada) {
                            const movimentosMinuta = MovimentoCNJStore.movimentosMinutas.find(
                                mp => mp.numMinuta === minutaSelecionada.numero
                            );

                            runInAction(() => {
                                if (!movimentosMinuta) {
                                    movimentosCriados.forEach((mov, idx) => (mov.id = idx));
                                    MovimentoCNJStore.movimentosMinutas.push({
                                        numMinuta: minutaSelecionada.numero,
                                        movimentos: movimentosCriados,
                                    });
                                    MovimentoCNJStore.quantidadeMovimentosIncluidos += movimentosCriados.length;
                                } else {
                                    const baseId = movimentosMinuta.movimentos.length;
                                    movimentosCriados.forEach((mov, idx) => (mov.id = baseId + idx));
                                    movimentosMinuta.movimentos.push(...movimentosCriados);
                                    MovimentoCNJStore.quantidadeMovimentosIncluidos += movimentosCriados.length;
                                }
                                MovimentoCNJStore.sameState = false;
                            });
                        }
                    } else {
                        // fluxo sem variáveis relevantes continua igual
                        const minutaSelecionada = this.getMinutaSelecionada(processo);
                        if (minutaSelecionada) {
                            const movimentosMinuta = MovimentoCNJStore.movimentosMinutas.find(
                                mp => mp.numMinuta === minutaSelecionada.numero
                            );
                            if (!movimentosMinuta) {
                                runInAction(() => {
                                    movSelecionado.id = MovimentoCNJStore.movimentosMinutas.length - 1;
                                    MovimentoCNJStore.movimentosMinutas.push({
                                        numMinuta: minutaSelecionada.numero,
                                        movimentos: [movSelecionado],
                                    });
                                    MovimentoCNJStore.quantidadeMovimentosIncluidos++;
                                });
                            } else {
                                runInAction(() => {
                                    movSelecionado.id = movimentosMinuta.movimentos.length - 1;
                                    movimentosMinuta.movimentos.push(movSelecionado);
                                    MovimentoCNJStore.quantidadeMovimentosIncluidos++;
                                });
                            }
                        }
                    }
                });
        } else {
            const complementosPossiveis = await this.recuperarOpcoesEComplementos(MovimentoCNJStore.processos[0], MovimentoCNJStore.movimentoPJeSelecionado, false)
            runInAction(() => {
                MovimentoCNJStore.movimentoPJeSelecionado.complementosPossiveis = complementosPossiveis
            })
            runInAction(() => {
                if (complementosPossiveis.length) {
                    MovimentoCNJStore.openModalComplementos = true;
                } else {
                    this.continuarInclusao();
                }
            })
        }
        runInAction(() => {
            MovimentoCNJStore.sameState = false;
        })
    };

    async continuarInclusao() {
        MovimentoCNJStore.carregandoComplementosAutomaticos = true;
        if (MovimentoCNJStore.complementosAutomaticosSetados) {
            setTimeout(() => {
                MensagemStore.apresentarAviso(
                    'Ao confirmar sua ação, os complementos serão preenchidos automaticamente.'
                );
            }, 100);
            runInAction(() => {
                MovimentoCNJStore.complementosAutomaticosSetados = false;
            })
        }
        for (const processo of MovimentoCNJStore.processos) {
            await ProcessoService.carregarPartesProcesso(processo);
            let movimentos: MovimentoGEDTO[] = [];
            const movSelecionado = MovimentoGEDTO.novo(
                processo.ehLegado ? MovimentoCNJStore.movimentoLegadoSelecionado : MovimentoCNJStore.movimentoPJeSelecionado,
                processo
            );
            const minutaSelecionada = this.getMinutaSelecionada(processo);
            if (!minutaSelecionada) continue;
            const movimentosMinuta = MovimentoCNJStore.movimentosMinutas.find(mp => mp.numMinuta === minutaSelecionada.numero);
            const descricaoTemComplemento = this.descricaoContemVariavelRelevante(movSelecionado.descricaoMovimento);
            const temParteSelecionadaManualmente = this.existeComplementoEmProcessamento('nome da parte');
            const deveSetarComplementosAutomaticos = descricaoTemComplemento
                && movSelecionado.descricaoMovimento?.includes('#{nome da parte}')
                && !temParteSelecionadaManualmente;
            if (descricaoTemComplemento && processo.ehPje) {
                if (deveSetarComplementosAutomaticos) {
                    runInAction(() => {
                        MovimentoCNJStore.complementosAutomaticosSetados = true;
                    });
                }

                const complementosPossiveis = await this.recuperarOpcoesEComplementos(processo, movSelecionado, false);

                if (MovimentoCNJStore.polosSelecionados.length > 0) {
                    movimentos = this.criarMovimentosComplementados(processo, movSelecionado, complementosPossiveis);
                }

                runInAction(() => {
                    if (!movimentosMinuta) {
                        const novaId = MovimentoCNJStore.movimentosMinutas.length - 1;
                        movimentos.forEach(mov => {
                            mov.id = novaId;
                        });
                        movSelecionado.id = novaId;

                        if (deveSetarComplementosAutomaticos) {
                            this.setarComplementosAutomaticos(movimentos);
                        }

                        MovimentoCNJStore.movimentosMinutas.push({
                            numMinuta: minutaSelecionada.numero,
                            movimentos,
                        });

                        minutaSelecionada.movimentosCNJ = movimentos;
                        MovimentoCNJStore.openModalComplementos = false;
                    } else {
                        if (deveSetarComplementosAutomaticos) {
                            this.setarComplementosAutomaticos(movimentos);
                        }
                        const movimentosNovos = movimentos.filter(novo =>
                            !movimentosMinuta.movimentos.some(existente =>
                                this.movimentosSaoIguais(existente, novo)
                            )
                        );

                        movimentosMinuta.movimentos = movimentosMinuta.movimentos.concat(movimentosNovos);
                        MovimentoCNJStore.quantidadeMovimentosIncluidos++;
                    }

                    MovimentoCNJStore.sameState = false;
                });
            } else {
                runInAction(() => {
                    if (!movimentosMinuta) {
                        movSelecionado.id = MovimentoCNJStore.movimentosMinutas.length - 1;
                        MovimentoCNJStore.movimentosMinutas.push({ numMinuta: minutaSelecionada.numero, movimentos: [movSelecionado] });
                        MovimentoCNJStore.quantidadeMovimentosIncluidos++;
                    } else {
                        movSelecionado.id = movimentosMinuta.movimentos.length - 1;
                        movimentosMinuta.movimentos.push(movSelecionado);
                        MovimentoCNJStore.quantidadeMovimentosIncluidos++;
                    }
                });
            }
        }
        runInAction(() => {
            MovimentoCNJStore.carregandoComplementosAutomaticos = false;
            MovimentoCNJStore.polosSelecionados = [];
            MovimentoCNJStore.complementosMovimentoProcessoEmProcessamento = [];
            MovimentoCNJStore.movimentoLegadoSelecionado = null;
            MovimentoCNJStore.movimentoPJeSelecionado = null;
        })
    }

    private movimentosSaoIguais(a: MovimentoGEDTO, b: MovimentoGEDTO): boolean {
        if (a.codMovimentoCnj !== b.codMovimentoCnj) return false;

        const compA = (a.complementos || [])
            .map(c => `${c.stringComplemento}:${c.valorSetado || c.valorComplemento || c.valor}`)
            .sort()
            .join('|');

        const compB = (b.complementos || [])
            .map(c => `${c.stringComplemento}:${c.valorSetado || c.valorComplemento || c.valor}`)
            .sort()
            .join('|');

        return compA === compB;
    }

    private existeComplementoEmProcessamento(nomeComplemento: string): boolean {
        return MovimentoCNJStore.complementosMovimentoProcessoEmProcessamento?.some(complemento =>
            complemento.stringComplemento === nomeComplemento
            && (complemento.valorSetado || complemento.valor || complemento.valorComplemento)
        );
    }


    normalizarPolo(polo: string): string {
        switch (polo.toLowerCase()) {
            case 'polo ativo':
                return 'ATIVO';
            case 'polo passivo':
                return 'PASSIVO';
            case 'outros participantes':
                return 'TERCEIROS';
            default:
                return polo;
        }
    }

    abreviarNome(nomeCompleto) {
        if (!nomeCompleto) return "";

        let nomeAbreviado = "";
        const partesDosNomes = nomeCompleto.split(/\s+/);

        for (const parteDoNome of partesDosNomes) {
            if (parteDoNome.length > 0) {
                const lower = parteDoNome.toLowerCase();

                if (parteDoNome.includes(".")) {
                    nomeAbreviado += parteDoNome + " ";
                } else if (["de", "da", "das", "do", "dos"].includes(lower)) {
                    nomeAbreviado += parteDoNome + " ";
                } else {
                    nomeAbreviado += parteDoNome.charAt(0) + ". ";
                }
            }
        }

        return nomeAbreviado.trim();
    }

    criarMovimentosComplementados(processo: any, movSelecionado: any, complementosPossiveis: any[]): MovimentoGEDTO[] {
        const movimentos: MovimentoGEDTO[] = [];
        const recursoSelecionado = MovimentoCNJStore.complementosMovimentoProcessoEmProcessamento.find(
            c => c.stringComplemento === 'nome do recurso'
        );

        const exigeNomeDaParte = movSelecionado.descricaoMovimentoUnificacao.includes('#{nome da parte}');
        const exigeNomeDoRecurso = movSelecionado.descricaoMovimentoUnificacao.includes('#{nome do recurso}');

        if (exigeNomeDaParte) {
            // Verifica se há complementos manualmente selecionados na store
            const complementoParteSelecionado = MovimentoCNJStore.complementosMovimentoProcessoEmProcessamento.find(
                c => c.stringComplemento === 'nome da parte'
            );

            // SE HÁ COMPLEMENTO SELECIONADO MANUALMENTE
            if (complementoParteSelecionado) {
                // Busca o complemento correspondente nos complementos possíveis
                const complementoTipoParte = complementosPossiveis.find(c => c.stringComplemento === 'nome da parte');
                if (!complementoTipoParte) return movimentos;

                // Procura a opção correspondente ao valor selecionado
                const opcaoParte = complementoTipoParte.opcoes.find(op => {
                    const valorOpcao = op.valor?.valorComplemento || op.valor?.valor || op.valor;
                    const valorSelecionado = complementoParteSelecionado.valorComplemento || complementoParteSelecionado.valor;
                    return valorOpcao === valorSelecionado;
                });

                if (!opcaoParte) return movimentos;

                const valorParteClone = JSON.parse(JSON.stringify(opcaoParte.valor));
                const complementoParte = {
                    ...valorParteClone,
                    stringComplemento: 'nome da parte',
                    valorSetado: valorParteClone.valorComplemento || valorParteClone.valor,
                };

                const complementosDoMovimento = [complementoParte];

                if (exigeNomeDoRecurso && recursoSelecionado) {
                    complementosDoMovimento.push({
                        codElementoDominio: recursoSelecionado.codElementoDominio,
                        codComplementoCnj: recursoSelecionado.codComplementoCnj,
                        valorComplemento: recursoSelecionado.valorComplemento,
                        stringComplemento: recursoSelecionado.stringComplemento,
                        valorSetado: recursoSelecionado.valor,
                    });
                }

                movimentos.push(MovimentoGEDTO.novo({
                    ...toJS(movSelecionado),
                    complementos: complementosDoMovimento,
                    complementosPossiveis,
                    origem: 'P'
                }));
            }
            // SE NÃO HÁ SELEÇÃO MANUAL, APENAS PARA POLO ATIVO
            else {
                Object.entries(VARIAVEIS_COMPLEMENTO_POR_POLO).forEach(([variavel, polos]) => {
                    if (variavel !== 'nome da parte') return;

                    // Filtra apenas polo ATIVO para comportamento automático
                    const polosFiltrados = polos.filter(polo => polo === 'polo ativo');

                    polosFiltrados.forEach(polo => {
                        if (!MovimentoCNJStore.polosSelecionados.includes(polo)) return;

                        const chavePolo = this.normalizarPolo(polo);
                        const partes = processo.partes[chavePolo];
                        if (!partes || partes.length === 0) return;

                        partes.forEach((parte: any) => {
                            const nomeParte = parte.nome.trim().toLowerCase();
                            const complementoTipoParte = complementosPossiveis.find(c => c.stringComplemento === 'nome da parte');
                            if (!complementoTipoParte) return;

                            const opcaoParte = complementoTipoParte.opcoes.find(op =>
                                processo.segredoDeJustica ? this.abreviarNome(op.texto.toLowerCase()).includes(nomeParte) : op.texto.toLowerCase().includes(nomeParte)
                            );
                            if (!opcaoParte) return;

                            const valorParteClone = JSON.parse(JSON.stringify(opcaoParte.valor));
                            const complementoParte = {
                                ...valorParteClone,
                                stringComplemento: 'nome da parte',
                                valorSetado: valorParteClone.valorComplemento || valorParteClone.valor,
                            };

                            const complementosDoMovimento = [complementoParte];

                            if (exigeNomeDoRecurso && recursoSelecionado) {
                                complementosDoMovimento.push({
                                    codElementoDominio: recursoSelecionado.codElementoDominio,
                                    codComplementoCnj: recursoSelecionado.codComplementoCnj,
                                    valorComplemento: recursoSelecionado.valorComplemento,
                                    stringComplemento: recursoSelecionado.stringComplemento,
                                    valorSetado: recursoSelecionado.valor,
                                });
                            }

                            movimentos.push(MovimentoGEDTO.novo({
                                ...toJS(movSelecionado),
                                complementos: complementosDoMovimento,
                                complementosPossiveis,
                                origem: 'P'
                            }));
                        });
                    });
                });
            }
        } else if (exigeNomeDoRecurso && recursoSelecionado) {
            const complementoTipo = complementosPossiveis.find(c => c.stringComplemento === 'nome do recurso');
            if (!complementoTipo) return movimentos;

            const opcao = complementoTipo.opcoes.find(op =>
                op.texto.toLowerCase() === recursoSelecionado.valor.toLowerCase()
            );
            if (!opcao) return movimentos;

            const valorClone = JSON.parse(JSON.stringify(opcao.valor));
            const complementoRecurso = {
                ...valorClone,
                stringComplemento: 'nome do recurso',
                valorSetado: valorClone.valorComplemento || valorClone.valor,
            };

            movimentos.push(MovimentoGEDTO.novo({
                ...toJS(movSelecionado),
                complementos: [complementoRecurso],
                complementosPossiveis,
                origem: 'P'
            }));
        }

        return movimentos;
    }

    setarComplementosAutomaticos(movimentos: MovimentoGEDTO[]) {
        movimentos.forEach(mov => {
            mov.complementosAutomaticosSetados = true;
            mov.complementos.forEach(complemento => {
                complemento.valorSetado = complemento.valorComplemento;
            });
        });
    }

    complementosPreenchidos(movimentos) {
        let naoPrecisaDeComplementos = true;
        movimentos.forEach(mov => {
            const movimentoCNJ = MovimentoCNJStore.movimentos.find(movcnj => movcnj.codMovimentoCnj === mov.codMovimentoCnj);
            const complementoExcludente = this.getMovimentosExcludentes(movimentoCNJ);
            let isComplementosCompletos;
            if (complementoExcludente?.length > 0) {
                const complementosQueDevemSerPreenchidos = movimentoCNJ?.variaveis.length - complementoExcludente?.length;
                isComplementosCompletos = complementosQueDevemSerPreenchidos === mov.complementos?.length;
            } else {
                isComplementosCompletos = movimentoCNJ?.variaveis?.length === mov.complementos?.length;
            }
            naoPrecisaDeComplementos = naoPrecisaDeComplementos && isComplementosCompletos;
        });

        return naoPrecisaDeComplementos;
    }

    removerMovimentoSelecionado = (movimentoSelecionado: MovimentoGEDTO, _legado: boolean) => {
        MovimentoCNJStore.processos.forEach(processo => {
            this.removerMovimento(processo, movimentoSelecionado, false);
        });
    }

    private movimentoSelecionadoParaRemocao(movimento: MovimentoGEDTO, movimentoSelecionado: MovimentoGEDTO): boolean {
        if (movimento === movimentoSelecionado) {
            return true;
        }

        if (movimento.id != null && movimentoSelecionado.id != null) {
            return movimento.id === movimentoSelecionado.id
                && movimento.codMovimentoCnj === movimentoSelecionado.codMovimentoCnj;
        }

        return movimento.codMovimentoCnj === movimentoSelecionado.codMovimentoCnj;
    }

    removerMovimento = (processo: Processo, movimentoSelecionado: MovimentoGEDTO, removerApenasUmaOcorrencia = true) => {
        const minutaSelecionada = this.getMinutaSelecionada(processo);
        if (!minutaSelecionada) {
            return;
        }

        let algumMovimentoRemovido = false;
        MovimentoCNJStore.movimentosMinutas.forEach(mm => {
            if (mm.numMinuta == minutaSelecionada.numero) {
                const removerPorReferencia = mm.movimentos.some(mov => mov === movimentoSelecionado);
                let movimentoRemovido = false;
                const movimentosAtualizados = mm.movimentos.filter(mov => {
                    const movimentoSelecionadoParaRemocao = removerPorReferencia
                        ? mov === movimentoSelecionado
                        : this.movimentoSelecionadoParaRemocao(mov, movimentoSelecionado);
                    const deveRemover = movimentoSelecionadoParaRemocao
                        && (!removerApenasUmaOcorrencia || !movimentoRemovido);

                    if (deveRemover) {
                        movimentoRemovido = true;
                        return false;
                    }

                    return true;
                });

                if (movimentoRemovido) {
                    const quantidadeRemovida = mm.movimentos.length - movimentosAtualizados.length;
                    mm.movimentos = movimentosAtualizados;
                    minutaSelecionada.movimentosCNJ = movimentosAtualizados;
                    MovimentoCNJStore.quantidadeMovimentosRemovidos += quantidadeRemovida;
                    algumMovimentoRemovido = true;
                }
            }
        });
        if (algumMovimentoRemovido) {
            MovimentoCNJStore.sameState = false;
        }
    };
}

export default new MovimentoService();
